import React, { FC, memo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
// mui
import { Box, Grid } from '@mui/material';
// components
import { Variants } from '../variants';
import OutlinedInputController from 'controllers/outlined-input-controller';
import QuantityInput from '../quantity-input';
import SelectionImage from '../selection-image';
import InputController from '../../../../../pages/feedback/components/modals/components/input-controller';
import UploadFile from '../upload-file/upload-file';
import UploadedFiles from '../upload-file/uploaded-files';
import { StyledLabelNewUI } from '../../../../../components/inputs/outlined-input';
import AmountInput from '../amount-input';
// types
import { ISelectionFormOptions } from '../../../utils/types';
// api
import { ISelectionUploadingFile } from 'redux-store/api/selections';
import { DeleteButton } from 'components/delete-button/delete-button';

interface IProps {
  optionIndex: number;
  projectId: number;
  isSelectionApproved?: boolean;
  isProjectSelection?: boolean;
  isDeleteOptionVisible: boolean;
  isDescriptionEditDisable?: boolean;
  deleteOption: (orderNumber: number) => void;
}

const SelectionOptionContent: FC<IProps> = ({
  optionIndex,
  projectId,
  isSelectionApproved = false,
  isProjectSelection,
  isDescriptionEditDisable,
  isDeleteOptionVisible,
  deleteOption,
}) => {
  const form = useFormContext<ISelectionFormOptions>();

  const {
    fields: uploadedFiles,
    remove,
    append,
    update,
  } = useFieldArray({
    control: form.control,
    name: `options.${optionIndex}.uploads`,
    keyName: 'key',
  });

  const deleteFile = (fileId: number | string): void => {
    const fileIndex = uploadedFiles.findIndex(
      ({ id, tempId }) => fileId === id || fileId === tempId
    );

    if (fileIndex >= 0) {
      remove(fileIndex);
    }
  };

  const appendFiles = (files: ISelectionUploadingFile[]): void => {
    append(files);
  };

  const updateFile = (tempId: string, updatingData: Partial<ISelectionUploadingFile>): void => {
    const uploads = form.watch(`options.${optionIndex}.uploads`);

    const position = uploads.findIndex((file) => file.tempId === tempId);

    if (position >= 0) {
      update(position, {
        ...uploads[position],
        ...updatingData,
      });
    }
  };

  const renderAttachmentsBox = () => {
    return (
      <Box>
        <StyledLabelNewUI>Attachments</StyledLabelNewUI>
        <UploadFile appendFiles={appendFiles} updateFile={updateFile} projectId={projectId} />
        <UploadedFiles
          uploadingFiles={uploadedFiles}
          handleDeleteUploadedData={deleteFile}
          canDelete={true}
          sx={{ background: '#FFF' }}
        />
      </Box>
    );
  };

  return (
    <>
      <SelectionImage
        optionIndex={optionIndex}
        isDescriptionEditDisable={isDescriptionEditDisable}
        projectId={projectId}
      />

      <Grid
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: 'column',
          gap: '12px',
          marginTop: '20px',
        }}
      >
        <Grid
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            columnGap: '16px',
            rowGap: '12px',
          }}
        >
          <OutlinedInputController
            fullWidth
            withError
            name={`options.${optionIndex}.title`}
            label="Item Name"
            placeholder=""
            disabled={isSelectionApproved}
          />

          <OutlinedInputController
            fullWidth
            withError
            optional
            name={`options.${optionIndex}.url`}
            label="URL"
            placeholder="https://example.com"
            disabled={isSelectionApproved}
          />

          <AmountInput name={`options.${optionIndex}.amount`} disabled={isSelectionApproved} />

          <QuantityInput
            name={`options.${optionIndex}.quantity`}
            disabled={isSelectionApproved}
            disableUpArrow={isSelectionApproved}
            disableDownArrow={isSelectionApproved}
          />
        </Grid>

        <InputController
          optional
          withError
          name={`options.${optionIndex}.description`}
          label="Item Details/Specs"
          placeholder="Enter description here"
          disabled={isDescriptionEditDisable}
        />

        {renderAttachmentsBox()}

        <Variants isSelectionApproved={isSelectionApproved} optionIndex={optionIndex} />

        {isDeleteOptionVisible && (
          <DeleteButton text="Delete option" onClick={() => deleteOption(optionIndex)} />
        )}
      </Grid>
    </>
  );
};

export default memo(SelectionOptionContent);
